import React, { useState, useEffect } from "react";

interface ServiceType {
  id: number;
  name: string;
  category: "consultation" | "machine" | "treatment";
  description?: string;
  duration: number;
  requires_equipment: boolean;
  is_active: boolean;
}

interface ServiceTypeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingServiceType?: ServiceType | null;
}

const ServiceTypeForm: React.FC<ServiceTypeFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingServiceType,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    category: "consultation" as "consultation" | "machine" | "treatment",
    description: "",
    duration: "30",
    requires_equipment: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Color theme
  const colorTheme = {
    primary: "#667eea",
    success: "#10b981",
    error: "#ef4444",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  // Service categories with icons and descriptions
  const serviceCategories = [
    {
      value: "consultation",
      label: "Consultation",
      icon: "👨‍⚕️",
      description: "Doctor consultations and medical advice",
      defaultDuration: 30,
      requiresEquipment: false,
    },
    {
      value: "machine",
      label: "Machine Service",
      icon: "🔬",
      description: "Services requiring medical equipment",
      defaultDuration: 20,
      requiresEquipment: true,
    },
    {
      value: "treatment",
      label: "Treatment",
      icon: "🩺",
      description: "Medical treatments and procedures",
      defaultDuration: 45,
      requiresEquipment: false,
    },
  ];

  // Initialize form when editing or when modal opens
  useEffect(() => {
    if (editingServiceType) {
      setFormData({
        name: editingServiceType.name,
        category: editingServiceType.category,
        description: editingServiceType.description || "",
        duration: editingServiceType.duration.toString(),
        requires_equipment: editingServiceType.requires_equipment,
      });
    } else {
      // Reset form for new service type
      setFormData({
        name: "",
        category: "consultation",
        description: "",
        duration: "30",
        requires_equipment: false,
      });
    }
    setErrors({});
  }, [editingServiceType, isOpen]);

  // Handle category change
  const handleCategoryChange = (category: string) => {
    const categoryData = serviceCategories.find(
      (cat) => cat.value === category
    );
    if (categoryData) {
      setFormData((prev) => ({
        ...prev,
        category: category as any,
        duration: categoryData.defaultDuration.toString(),
        requires_equipment: categoryData.requiresEquipment,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    try {
      const method = editingServiceType ? "PUT" : "POST";
      const url = editingServiceType
        ? `${API_BASE}/service-types/${editingServiceType.id}`
        : `${API_BASE}/service-types`;

      // Prepare data
      const submitData = {
        ...formData,
        duration: parseInt(formData.duration),
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        if (errorData.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({
            submit: errorData.error || "Failed to save service type",
          });
        }
      }
    } catch (error) {
      console.error("Error saving service type:", error);
      setErrors({ submit: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: colorTheme.cardBg,
          borderRadius: "16px",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: `1px solid ${colorTheme.gray200}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: colorTheme.gray900,
                margin: "0 0 4px 0",
              }}
            >
              {editingServiceType
                ? "Edit Service Type"
                : "Add New Service Type"}
            </h3>
            <p
              style={{
                color: colorTheme.gray500,
                fontSize: "14px",
                margin: 0,
              }}
            >
              Configure service type details and default settings
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "none",
              background: colorTheme.gray100,
              color: colorTheme.gray500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form Content */}
        <div style={{ padding: "32px" }}>
          {/* Service Name */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Service"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Service Type Name{" "}
              <span style={{ color: colorTheme.error }}>*</span>
            </label>
            <input
              type="text"
              id="service_type_name"
              name="service_type_name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${
                  errors.name ? colorTheme.error : colorTheme.gray200
                }`,
                borderRadius: "8px",
                fontSize: "14px",
              }}
              placeholder="Enter service type name..."
              required
            />

            {errors.name && (
              <p
                style={{
                  color: colorTheme.error,
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                {errors.name}
              </p>
            )}
          </div>

          {/* Category Selection */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Category"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "12px",
              }}
            >
              Category <span style={{ color: colorTheme.error }}>*</span>
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "12px",
              }}
            >
              {serviceCategories.map((category) => (
                <div
                  key={category.value}
                  onClick={() => handleCategoryChange(category.value)}
                  style={{
                    padding: "16px",
                    border: `2px solid ${
                      formData.category === category.value
                        ? colorTheme.primary
                        : colorTheme.gray200
                    }`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    background:
                      formData.category === category.value
                        ? `${colorTheme.primary}15`
                        : colorTheme.cardBg,
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ fontSize: "24px", marginBottom: "8px" }}>
                    {category.icon}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color:
                        formData.category === category.value
                          ? colorTheme.primary
                          : colorTheme.gray900,
                      marginBottom: "4px",
                    }}
                  >
                    {category.label}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: colorTheme.gray500,
                      lineHeight: "1.3",
                    }}
                  >
                    {category.description}
                  </div>
                </div>
              ))}
            </div>
            {errors.category && (
              <p
                style={{
                  color: colorTheme.error,
                  fontSize: "12px",
                  marginTop: "4px",
                }}
              >
                {errors.category}
              </p>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="Description"
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: colorTheme.gray600,
                marginBottom: "8px",
              }}
            >
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              style={{
                width: "100%",
                padding: "12px",
                border: `2px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                fontSize: "14px",
                resize: "none",
              }}
              placeholder="Optional description of the service type..."
            />
          </div>

          {/* Duration & Equipment Settings */}
          <div
            style={{
              background: colorTheme.gray50,
              borderRadius: "12px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <h4
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: colorTheme.gray900,
                marginBottom: "16px",
              }}
            >
              Service Settings
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <label
                  htmlFor="Default"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colorTheme.gray600,
                    marginBottom: "6px",
                  }}
                >
                  Default Duration (minutes)
                </label>

                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `2px solid ${
                      errors.duration ? colorTheme.error : colorTheme.gray200
                    }`,
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  min="1"
                  max="480"
                  required
                />

                {errors.duration && (
                  <p
                    style={{
                      color: colorTheme.error,
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.duration}
                  </p>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <div>
                  <label
                    htmlFor="requires_equipment"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: colorTheme.gray600,
                      marginBottom: "6px",
                    }}
                  >
                    Equipment Required
                  </label>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: colorTheme.gray100,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="requires_equipment"
                      name="requires_equipment"
                      checked={formData.requires_equipment}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          requires_equipment: e.target.checked,
                        }))
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <label htmlFor="requires_equipment">
                      Requires medical equipment
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Equipment Info */}
            {formData.requires_equipment && (
              <div
                style={{
                  padding: "12px",
                  background: `${colorTheme.primary}15`,
                  borderRadius: "8px",
                  border: `1px solid ${colorTheme.primary}30`,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: colorTheme.gray600,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span>ℹ️</span>
                  Services of this type will require equipment selection and may
                  include equipment sponser revenue sharing.
                </p>
              </div>
            )}
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div
              style={{
                padding: "12px 16px",
                background: "#fecaca",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "14px",
                marginBottom: "24px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Form Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              paddingTop: "20px",
              borderTop: `1px solid ${colorTheme.gray200}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "12px 24px",
                border: `1px solid ${colorTheme.gray200}`,
                borderRadius: "8px",
                background: colorTheme.cardBg,
                color: colorTheme.gray600,
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !formData.name.trim() || !formData.duration}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background:
                  loading || !formData.name.trim() || !formData.duration
                    ? colorTheme.gray200
                    : colorTheme.success,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor:
                  loading || !formData.name.trim() || !formData.duration
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  loading || !formData.name.trim() || !formData.duration
                    ? 0.6
                    : 1,
              }}
            >
              {loading
                ? "Saving..."
                : editingServiceType
                ? "Update Service Type"
                : "Create Service Type"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypeForm;
